/**
 * HOD Forecast Digest page (`/hod/digest`) — Sprint 7 F1.
 *
 * Shows a coverage-breach digest for a HOD's teams over a configurable
 * forecast window (7 / 14 / 21 / 30 days, default 14).
 *
 * Role access:
 *   - HOD: views own digest (hod_id = session employee_id)
 *   - HR / Director / Exec Director: pick any HOD from the dropdown
 *     (populated from `GET /api/v1/employees?role=hod&active=true`).
 *
 * Feature flag: flags.hodForecastDigest (defaults OFF).
 */
import { useState } from 'react'
import { Navigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { flags } from '../../lib/feature-flags'
import { useSession } from '../../auth/auth-context'
import { getForecastDigest } from '../../api/forecast'
import { listEmployeesPage } from '../../api/employees'
import { ForecastTeamCard } from '../../components/hod/ForecastTeamCard'
import { PageHead } from '../../components/primitives/PageHead'
import { Banner } from '../../components/primitives/Banner'
import { Skeleton } from '../../components/primitives/Skeleton'
import { CalcPanel } from '../../components/primitives/CalcPanel'

// ── Constants ──────────────────────────────────────────────────────────────────

const ALLOWED_ROLES: ReadonlyArray<string> = ['hod', 'hr', 'director', 'exec_director']

const WINDOW_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 7, label: '7d' },
  { value: 14, label: '14d' },
  { value: 21, label: '21d' },
  { value: 30, label: '30d' },
]

// ── Inner page (session available) ────────────────────────────────────────────

interface HodDigestInnerProps {
  sessionEmployeeId: string
  sessionRole: string
}

function HodDigestInner({ sessionEmployeeId, sessionRole }: HodDigestInnerProps): JSX.Element {
  const isHod = sessionRole === 'hod'
  const [windowDays, setWindowDays] = useState<number>(14)
  // HR / Director / Exec Director pick a HOD from the dropdown
  const [pickedHodId, setPickedHodId] = useState<string>('')

  // Load active HODs for the picker (HR+ only; gated by role check above).
  // Limit 200 covers any realistic count of HODs in one tenant.
  const { data: hodList, isLoading: hodListLoading, error: hodListError } = useQuery({
    queryKey: ['employees', 'hods'],
    queryFn: () =>
      listEmployeesPage({ role: 'hod', active: true, limit: 200 }),
    enabled: !isHod,
    staleTime: 60_000,
  })

  const hodId = isHod ? sessionEmployeeId : pickedHodId

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['forecast', 'digest', hodId, windowDays],
    queryFn: () => getForecastDigest({ hod_id: hodId, window_days: windowDays }),
    enabled: hodId.length > 0,
    retry: false,
  })

  return (
    <>
      <PageHead
        eyebrow="Head of Department"
        title="Forecast digest"
        subtitle={
          data
            ? `${data.hod_name} — ${data.window_start} to ${data.window_end}`
            : 'Coverage-breach forecast across your teams'
        }
      />

      {/* HOD picker (non-HOD roles only) */}
      {!isHod ? (
        <div
          data-testid="hod-id-input-wrapper"
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <label
            htmlFor="dev-hod-id"
            style={{ fontSize: 12, color: 'var(--text-2)', flexShrink: 0 }}
          >
            Head of Department:
          </label>
          <select
            id="dev-hod-id"
            data-testid="dev-hod-id-input"
            value={pickedHodId}
            onChange={(e) => setPickedHodId(e.target.value)}
            disabled={hodListLoading || hodListError != null}
            style={{
              flex: 1,
              padding: '5px 10px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 13,
              background: 'var(--surface)',
              color: 'var(--text)',
              minHeight: 32,
              cursor: hodListLoading ? 'wait' : 'pointer',
            }}
          >
            <option value="">
              {hodListLoading
                ? 'Loading HODs…'
                : hodListError != null
                  ? 'Failed to load HODs'
                  : '— Select a HOD —'}
            </option>
            {hodList?.data.map((hod) => (
              <option key={hod.id} value={hod.id}>
                {hod.name} — {hod.email}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {/* Window chips */}
      <div
        data-testid="window-chips"
        style={{ display: 'flex', gap: 6, marginBottom: 20 }}
      >
        {WINDOW_OPTIONS.map(({ value, label }) => {
          const active = windowDays === value
          return (
            <button
              key={value}
              type="button"
              data-testid={`window-chip-${value}`}
              onClick={() => setWindowDays(value)}
              style={{
                padding: '4px 14px',
                borderRadius: 20,
                border: `1.5px solid ${active ? 'var(--steel-blue)' : 'var(--border)'}`,
                background: active
                  ? 'var(--accent-soft)'
                  : 'var(--surface)',
                color: active ? 'var(--steel-blue)' : 'var(--text-2)',
                fontSize: 12,
                fontWeight: active ? 700 : 400,
                cursor: 'pointer',
                transition: 'all var(--motion-duration-fast, 80ms) ease',
                minHeight: 30,
              }}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* Loading skeleton — delay-debounced to avoid flash on warm cache */}
      {isLoading ? (
        <div
          data-testid="digest-skeleton"
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} loading={isLoading} height={64} radius={8} />
          ))}
        </div>
      ) : error != null ? (
        /* Error banner */
        <div data-testid="digest-error-banner">
          <Banner
            kind="warning"
            title="Failed to load forecast digest"
            subtitle={
              error instanceof Error ? error.message : 'Please try again.'
            }
            action={
              <button
                type="button"
                data-testid="digest-retry-btn"
                onClick={() => void refetch()}
                style={{
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-strong)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            }
          />
        </div>
      ) : data == null ? (
        /* No query yet (non-HOD with empty hod_id input) */
        <div
          data-testid="digest-no-query"
          style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}
        >
          Pick a Head of Department above to load their forecast digest.
        </div>
      ) : data.teams.length === 0 ? (
        /* Empty state */
        <div
          data-testid="digest-empty-state"
          style={{
            padding: '40px 24px',
            textAlign: 'center',
            color: 'var(--text-3)',
            fontSize: 14,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
          }}
        >
          No coverage breaches forecasted. The team looks well-covered.
        </div>
      ) : (
        <>
          {/* Summary CalcPanel */}
          <CalcPanel
            title="Breach summary"
            sub={`${windowDays}-day window`}
            style={{ marginBottom: 20 }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span
                data-testid="digest-total-breach-days"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 40,
                  fontWeight: 700,
                  color: 'var(--text)',
                  lineHeight: 1,
                }}
              >
                {data.total_breach_days}
              </span>
              <span style={{ fontSize: 14, color: 'var(--text-2)' }}>
                day{data.total_breach_days !== 1 ? 's' : ''} at or below team minimum
              </span>
            </div>
          </CalcPanel>

          {/* Per-team accordion list */}
          <div
            data-testid="digest-team-list"
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            {data.teams.map((team) => (
              <ForecastTeamCard key={team.team_id} team={team} />
            ))}
          </div>
        </>
      )}
    </>
  )
}

// ── Public export ──────────────────────────────────────────────────────────────

/**
 * HOD Forecast Digest page.
 *
 * Gated on `flags.hodForecastDigest` AND role in
 * (hod, hr, director, exec_director). HODs view their own digest;
 * HR+ can pick any HOD via the dropdown.
 */
export function HodDigestPage(): JSX.Element | null {
  const { user, isLoading } = useSession()

  if (!flags.hodForecastDigest) return <Navigate to="/manager" />

  if (isLoading) {
    return (
      <div data-testid="digest-auth-loading" style={{ padding: 24 }}>
        Loading…
      </div>
    )
  }

  if (!user || !ALLOWED_ROLES.includes(user.role)) {
    return (
      <div
        data-testid="digest-forbidden"
        style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-2)' }}
      >
        <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 28, fontWeight: 500 }}>
          403 — Forbidden
        </h1>
        <p>You don&apos;t have permission to view this page.</p>
      </div>
    )
  }

  return (
    <HodDigestInner
      sessionEmployeeId={user.id}
      sessionRole={user.role}
    />
  )
}

